import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNotifications } from '../hooks/useNotifications';
import SockJS from 'sockjs-client/dist/sockjs';

/**
 * Reliable SockJS probe function
 */
function probeSockJS(url) {
  return new Promise((resolve) => {
    const sock = new SockJS(url, null, { timeout: 5000, withCredentials: true });
    let finished = false;

    const finish = (ok, error = null) => {
      if (finished) return;
      finished = true;
      try { sock.close(); } catch {}
      resolve({ success: ok, error });
    };

    sock.onopen = () => finish(true);
    sock.onerror = (e) => finish(false, e?.message || 'connect failed');
    // transport/network timeouts
    setTimeout(() => finish(false, 'timeout'), 6000);
  });
}

const ConnectionDiagnostic = () => {
  const { user, isAuthenticated } = useSelector((s) => s.auth);
  const { isConnected, error } = useNotifications();
  const [apiStatus, setApiStatus] = useState('checking');
  const [wsStatus, setWsStatus] = useState('checking');

  useEffect(() => {
    (async () => {
      // API check
      try {
        const apiBase = 'http://localhost:8080';
        const r = await fetch(`${apiBase}/actuator/health`, { credentials: 'include' });
        setApiStatus(r.ok ? 'connected' : `error: ${r.status}`);
      } catch (e) {
        setApiStatus(`error: ${e.message}`);
      }

      // SockJS check
      const wsEndpoint = process.env.REACT_APP_WEBSOCKET_ENDPOINT || 'http://localhost:8080/ws';
      const { success, error } = await probeSockJS(wsEndpoint);
      setWsStatus(success ? 'connected' : `error: ${error || 'unknown'}`);
    })();
  }, []);

  if (!isAuthenticated || user?.role !== 'ADMIN') return null;

  const getStatusColor = (s) => (s === 'connected' ? 'text-green-600' : s === 'checking' ? 'text-yellow-600' : 'text-red-600');
  const getStatusIcon = (s) => (s === 'connected' ? '✅' : s === 'checking' ? '⏳' : '❌');

  return (
    <div className="fixed top-4 right-4 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-50 max-w-sm">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">Connection Status</h3>
      <div className="space-y-2 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">API:</span>
          <span className={`font-medium ${getStatusColor(apiStatus)}`}>{getStatusIcon(apiStatus)} {apiStatus}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-600">SockJS:</span>
          <span className={`font-medium ${getStatusColor(wsStatus)}`}>{getStatusIcon(wsStatus)} {wsStatus}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-600">STOMP:</span>
          <span className={`font-medium ${getStatusColor(isConnected ? 'connected' : 'error')}`}>
            {getStatusIcon(isConnected ? 'connected' : 'error')} {isConnected ? 'connected' : 'disconnected'}
          </span>
        </div>
        {error && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700">
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>
      <div className="mt-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
        <div><strong>API:</strong> {process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8080'}</div>
        <div><strong>SockJS:</strong> {process.env.REACT_APP_WEBSOCKET_ENDPOINT ?? 'http://localhost:8080/ws'}</div>
      </div>
    </div>
  );
};

export default ConnectionDiagnostic;
